import {
  validate,
  object,
  string,
  optional,
  enums,
  type Infer,
} from 'superstruct'
import { gesetzeFindenSites } from './jurisdiction.constants'

declare global {
  interface Window {
    wasAfterClick?: boolean
  }
}

const privateSchema = object({
  // Optional because only some runtimes hand credentials over as env vars. Lambda
  // and Vercel do; ECS Fargate instead exposes a credentials endpoint via
  // AWS_CONTAINER_CREDENTIALS_RELATIVE_URI, so requiring these would stop every
  // Fargate job at module load. The SDK's default provider chain covers both.
  AWS_ACCESS_KEY_ID: optional(string()),
  AWS_SECRET_ACCESS_KEY: optional(string()),
  // Not optional: every runtime sets it, tasks included (infra/jobs.ts pins it), and
  // `monitoring.ts` interpolates it straight into an S3 URL — an undefined region
  // there gets persisted as a permanently broken document link instead of failing.
  AWS_REGION: string(),
  TABLE_NAME: string(),
  BUCKET_NAME: string(),
  MATOMO_API_KEY: optional(string()),
  GENERATE_LAW_ABBREVIATIONS: optional(string()),
  /** Lambda that renders regulatory-monitoring Excel reports (on-demand exports). */
  GENERATE_RM_REPORT_FUNCTION_NAME: optional(string()),

  // OPENAI_KEY: string(),
  // CLERK_SECRET_KEY: string(),

  /** #alarms */
  SLACK_ALARM_CHANNEL_ID: string(),
  /** #frontend-alarms */
  SLACK_FRONTEND_ALARMS_CHANNEL_ID: string(),
  /** #logs */
  SLACK_LOGS_CHANNEL_ID: string(),
  SLACK_BOT_TOKEN: string(),
  TYPESENSE_HOST: string(),
  TYPESENSE_API_KEY: string(),
  VOYAGE_API_KEY: string(),
  DATABASE_URL: string(),
  INTERNAL_WORKFLOWS_QUEUE_URL: optional(string()),
  REVALIDATE_KEY_CODARA_DASHBOARD: optional(string()),
  REVALIDATE_KEY_INTERNAL_DASHBOARD: optional(string()),
  /**
   * Notion internal integration token. Optional: consumers that surface Notion
   * content (marketing blog, dashboard changelog) gate on `isNotionConfigured()`
   * so a missing token hides the feature instead of breaking the app.
   */
  NOTION_API_KEY: optional(string()),
  // RM_REPORT_CERHA_HEMPEL_EMAIL: string(),
})

const publicSchema = object({
  NEXT_PUBLIC_VERCEL_ENV: string(),
  NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF: string(),
  NEXT_PUBLIC_VERCEL_BRANCH_URL: string(),
  NEXT_PUBLIC_VERCEL_URL: string(),
  NEXT_PUBLIC_APP_NAME: string(),
  NEXT_PUBLIC_DEFAULT_JURISDICTION: optional(enums(gesetzeFindenSites)),
  /** Public bucket host for Notion images mirrored out of expiring URLs. */
  NEXT_PUBLIC_BUCKET_NAME: optional(string()),
})

type privateType = Infer<typeof privateSchema>

const publicenvVariables = {
  NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
  NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF:
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF,
  NEXT_PUBLIC_VERCEL_BRANCH_URL: process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL,
  NEXT_PUBLIC_VERCEL_URL: process.env.NEXT_PUBLIC_VERCEL_URL,
  NEXT_PUBLIC_APP_NAME: process.env.NEXT_PUBLIC_APP_NAME,
  NEXT_PUBLIC_DEFAULT_JURISDICTION:
    process.env.NEXT_PUBLIC_DEFAULT_JURISDICTION,
  NEXT_PUBLIC_BUCKET_NAME: process.env.NEXT_PUBLIC_BUCKET_NAME,
}

const privateEnvVariables = {
  AWS_ACCESS_KEY_ID: process.env.AWS_ACCESS_KEY_ID,
  AWS_SECRET_ACCESS_KEY: process.env.AWS_SECRET_ACCESS_KEY,
  AWS_REGION: process.env.AWS_REGION,
  TABLE_NAME: process.env.TABLE_NAME,
  BUCKET_NAME: process.env.BUCKET_NAME,
  MATOMO_API_KEY: process.env.MATOMO_API_KEY,
  GENERATE_LAW_ABBREVIATIONS: process.env.GENERATE_LAW_ABBREVIATIONS,
  GENERATE_RM_REPORT_FUNCTION_NAME:
    process.env.GENERATE_RM_REPORT_FUNCTION_NAME,

  VOYAGE_API_KEY: process.env.VOYAGE_API_KEY,
  // OPENAI_KEY: process.env.OPENAI_KEY,
  // CLERK_SECRET_KEY: process.env.CLERK_SECRET_KEY,
  SLACK_ALARM_CHANNEL_ID: process.env.SLACK_ALARM_CHANNEL_ID,
  SLACK_FRONTEND_ALARMS_CHANNEL_ID:
    process.env.SLACK_FRONTEND_ALARMS_CHANNEL_ID,
  SLACK_LOGS_CHANNEL_ID: process.env.SLACK_LOGS_CHANNEL_ID,
  SLACK_BOT_TOKEN: process.env.SLACK_BOT_TOKEN,
  TYPESENSE_HOST: process.env.TYPESENSE_HOST,
  TYPESENSE_API_KEY: process.env.TYPESENSE_API_KEY,
  DATABASE_URL: process.env.DATABASE_URL,
  INTERNAL_WORKFLOWS_QUEUE_URL: process.env.INTERNAL_WORKFLOWS_QUEUE_URL,
  REVALIDATE_KEY_CODARA_DASHBOARD: process.env.REVALIDATE_KEY_CODARA_DASHBOARD,
  REVALIDATE_KEY_INTERNAL_DASHBOARD:
    process.env.REVALIDATE_KEY_INTERNAL_DASHBOARD,
  NOTION_API_KEY: process.env.NOTION_API_KEY,
}

const [errPublic, envPublic] = validate(publicenvVariables, publicSchema)
if (errPublic !== undefined) {
  console.error(errPublic)
  throw Error(`Public env var missing: ${errPublic.message}`)
}
const [errPrivate, envPrivate] = validate(privateEnvVariables, privateSchema)
if (envPrivate === undefined && typeof window === 'undefined') {
  console.error(errPrivate)
  throw Error(`Private env var missing: ${errPrivate.message}`)
}
const env = { ...envPublic, ...(envPrivate as privateType) }
export { env }
